import React, { useEffect, useState, useContext } from "react";
import {
  Tabs,
  Table,
  Card,
  Button,
  Modal,
  Tag,
  Empty,
  Popconfirm,
  Spin,
  message,
  Typography,
  Space,
  Divider,
  Row,
  Col,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  BankOutlined,
  CreditCardOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import axios from "axios";
import { useSearchParams } from "react-router-dom";

import { useUserState, UserContext } from "../../../Contexts/UserContext";
import MyCard from "./MyCard";
import BankAccountManager from "./BankAccountManager";

const { Title, Text } = Typography;

const BASE_URL = import.meta.env.VITE_APP_BASE_URL;
const stripePromise = loadStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY);

// Roles that only RECEIVE money — they save bank accounts, not credit cards
const PAYOUT_ROLES = ["home_owner", "cleaning_company", "property_manager"];

const MyBillingDetail = () => {
  const { user } = useUserState();
  const { addCardModal, setAddCardModal } = useContext(UserContext);
  const [searchParams, setSearchParams] = useSearchParams();

  // true for home_owner, cleaning_company, property_manager
  const isPayoutRole = PAYOUT_ROLES.includes(user?.currentRole);

  // Cards state (only relevant for payer roles: tenant, relo)
  const [cardData, setCardData] = useState([]);
  const [cardsLoading, setCardsLoading] = useState(false);
  const [delCardLoading, setDelCardLoading] = useState(false);

  // Transactions state
  const [transactionList, setTransactionList] = useState([]);

  const headerJson = {
    "Content-Type": "application/json",
    Authorization: user?.token,
  };

  useEffect(() => {
    if (user?.token) {
      // Only fetch saved cards for payer roles (tenant / relo)
      if (!isPayoutRole) {
        getPaymentDetails();
      }
      getTransactionList();
    }
  }, [user?.token]);

  // =========================
  // CARD METHODS (payer roles only)
  // =========================
  const getPaymentDetails = async () => {
    setCardsLoading(true);
    try {
      const res = await axios.get(`${BASE_URL}/api/payment-method/get-list`, {
        headers: headerJson,
      });
      if (res.data?.success || res.data?.statusCode === 200) {
        setCardData(res.data?.data?.data || []);
      }
    } catch (error) {
      console.error("Error fetching cards:", error);
    } finally {
      setCardsLoading(false);
    }
  };

  const deleteCard = async (payment_method_id, stripe_customer_id) => {
    setDelCardLoading(true);
    try {
      await axios.delete(`${BASE_URL}/api/payment-method/detach`, {
        headers: headerJson,
        data: { payment_method_id, stripe_customer_id },
      });
      message.success("Card removed successfully");
      getPaymentDetails();
    } catch (error) {
      message.error("Failed to remove card");
    } finally {
      setDelCardLoading(false);
    }
  };

  // =========================
  // TRANSACTION METHODS (all roles)
  // =========================
  const getTransactionList = async () => {
    try {
      const roleApiMap = {
        home_owner: "/get-by-user-house-owner",
        property_manager: "/get-by-user-house-owner",
        cleaning_company: "/get-by-user-cleaning-owner",
        tenant: "/get-trnx-list",
        relo: "/get-trnx-list",
      };
      const endpoint = roleApiMap[user?.currentRole] || "/get-trnx-list";
      const res = await axios.get(`${BASE_URL}/api/transactions${endpoint}`, {
        headers: headerJson,
      });
      setTransactionList(res.data?.data || []);
    } catch (err) {
      console.log("Error fetching transaction history", err?.message);
    }
  };

  // =========================
  // REUSABLE TRANSACTION TABLE
  // =========================
  const TransactionHistoryTable = () => (
    <>
      <Divider style={{ margin: "40px 0 24px" }} />
      <div style={{ marginBottom: "16px" }}>
        <Title level={5}>Transaction History</Title>
      </div>
      <Table
        size="small"
        pagination={{ pageSize: 5 }}
        dataSource={transactionList}
        rowKey="id"
        columns={[
          {
            title: "Description",
            key: "desc",
            render: (_, record) => (
              <Space>
                <CheckCircleFilled style={{ color: "#52c41a" }} />
                <div>
                  <Text strong>Payment {record.status === "succeeded" ? "Done" : "Incomplete"}</Text>
                  <br />
                  <Text type="secondary" style={{ fontSize: "12px" }}>
                    {new Date(record.created_at).toLocaleString()}
                  </Text>
                </div>
              </Space>
            ),
          },
          {
            title: "Amount",
            key: "amount",
            align: "right",
            render: (_, record) => (
              <Text strong style={{ fontSize: "16px", color: "#0474ba" }}>
                ${(record.amount / 100).toFixed(2)}
              </Text>
            ),
          },
        ]}
        locale={{ emptyText: "No transaction history found." }}
      />
    </>
  );

  // =========================
  // BUILD TABS BASED ON ROLE
  // =========================
  const buildTabs = () => {
    const tabs = [];

    // ── Saved Cards tab — ONLY for payer roles (tenant, relo) ──
    if (!isPayoutRole) {
      tabs.push({
        key: "cards",
        label: (
          <span>
            <CreditCardOutlined />
            {" "}Saved Cards
          </span>
        ),
        children: (
          <div style={{ padding: "16px 0" }}>
            <div style={{ marginBottom: "24px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <Title level={5} style={{ margin: 0 }}>My Cards</Title>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => setAddCardModal(true)}
                style={{ borderRadius: "6px", backgroundColor: "#0474ba", borderColor: "#0474ba" }}
              >
                Add New Card
              </Button>
            </div>

            <Spin spinning={cardsLoading}>
              {cardData.length > 0 ? (
                <Row gutter={[16, 16]}>
                  {cardData.map((card, i) => (
                    <Col xs={24} sm={12} lg={8} key={i}>
                      <Card
                        size="small"
                        style={{
                          borderRadius: "10px",
                          border: "1px solid #f0f0f0",
                          background: "#fafafa",
                        }}
                        actions={[
                          <Popconfirm
                            title="Delete Card"
                            description="Are you sure you want to delete this card?"
                            onConfirm={() => deleteCard(card.id, card.customer)}
                            okText="Yes"
                            cancelText="No"
                          >
                            <DeleteOutlined key="delete" style={{ color: "#ff4d4f" }} />
                          </Popconfirm>,
                        ]}
                      >
                        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                          <div style={{ width: "45px", textAlign: "center" }}>
                            <img
                              src={
                                card.card.brand === "visa"
                                  ? "https://2.bp.blogspot.com/-E85z2lvyugo/Wxqgevf3MLI/AAAAAAAAA5Y/vfTQDWg8g9Ii--s6KhDC0yWb9_h7l47dwCLcBGAs/s1600/visa.png"
                                  : "https://1.bp.blogspot.com/-b_7SEUzJd4M/Wxqgerdr6xI/AAAAAAAAA5c/7b1znN3spZsGJUn7DlQh72hzbQoqmYEtACLcBGAs/s1600/mastercard.png"
                              }
                              alt="card-brand"
                              style={{ width: "100%", opacity: 0.8 }}
                            />
                          </div>
                          <div>
                            <Text strong>{card?.billing_details?.name || "Card Holder"}</Text>
                            <br />
                            <Text type="secondary">**** **** **** {card?.card?.last4}</Text>
                            <br />
                            <Text size="small" type="secondary" style={{ fontSize: "12px" }}>
                              Exp: {card?.card?.exp_month}/{card?.card?.exp_year}
                            </Text>
                          </div>
                        </div>
                      </Card>
                    </Col>
                  ))}
                </Row>
              ) : (
                <Empty description="No cards found" />
              )}
            </Spin>

            {/* Transaction history for payer roles is in this tab */}
            <TransactionHistoryTable />
          </div>
        ),
      });
    }

    // ── Bank Account tab — for ALL roles ──
    tabs.push({
      key: "payouts",
      label: (
        <span>
          <BankOutlined />
          {" "}Bank Account
        </span>
      ),
      children: (
        <div style={{ padding: "16px 0" }}>
          <Elements stripe={stripePromise}>
            <BankAccountManager
              role={isPayoutRole ? "homeowner" : "tenant"}
              user={user}
              headerJson={headerJson}
              searchParams={searchParams}
              setSearchParams={setSearchParams}
            />
          </Elements>

          {/* Transaction history for receiver roles is shown here */}
          {isPayoutRole && <TransactionHistoryTable />}
        </div>
      ),
    });

    return tabs;
  };

  return (
    <div className="main-box" style={{ padding: "24px" }}>
      <div style={{ marginBottom: "32px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div>
          <Title level={3} style={{ margin: 0 }}>Billing Details</Title>
          <Text type="secondary">
            {isPayoutRole
              ? "Manage your payout account and view payment history."
              : "Manage your payment methods and payout settings."}
          </Text>
        </div>
      </div>

      <Card style={{ borderRadius: "12px", boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
        <Tabs
          defaultActiveKey={isPayoutRole ? "payouts" : "cards"}
          items={buildTabs()}
        />
      </Card>

      {/* Add Card Modal — only rendered for payer roles (tenant / relo) */}
      {!isPayoutRole && (
        <Modal
          title="Add Payment Method"
          open={addCardModal}
          onCancel={() => setAddCardModal(false)}
          footer={null}
          width={700}
          destroyOnClose
        >
          <Elements stripe={stripePromise}>
            <MyCard
              token={user?.token}
              setTitle={() => { }}
              getPaymentDetails={getPaymentDetails}
              setIsCard={() => { }}
            />
          </Elements>
        </Modal>
      )}
    </div>
  );
};

export default MyBillingDetail;
